import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileAlt, faPlus, faCheck, faSave } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import { useToast } from 'context/providers/ToastProvider';
import FalconCloseButton from 'components/common/FalconCloseButton';

export const NOTE_COLOR_PRESETS = [
    {
        id: 'yellow',
        name: 'Amarillo',
        lightBg: '#fef3c7',
        lightBorder: '#fde68a',
        lightText: '#92400e',
        darkBg: 'rgba(245, 158, 11, 0.15)',
        darkBorder: 'rgba(245, 158, 11, 0.35)',
        darkText: '#fbbf24',
        hex: '#fef3c7'
    },
    {
        id: 'blue',
        name: 'Azul',
        lightBg: '#dbeafe',
        lightBorder: '#bfdbfe',
        lightText: '#1e40af',
        darkBg: 'rgba(59, 130, 246, 0.15)',
        darkBorder: 'rgba(59, 130, 246, 0.35)',
        darkText: '#60a5fa',
        hex: '#dbeafe'
    },
    {
        id: 'green',
        name: 'Verde',
        lightBg: '#d1fae5',
        lightBorder: '#a7f3d0',
        lightText: '#065f46',
        darkBg: 'rgba(16, 185, 129, 0.15)',
        darkBorder: 'rgba(16, 185, 129, 0.35)',
        darkText: '#34d399',
        hex: '#d1fae5'
    },
    {
        id: 'orange',
        name: 'Naranja',
        lightBg: '#ffedd5',
        lightBorder: '#fed7aa',
        lightText: '#9a3412',
        darkBg: 'rgba(249, 115, 22, 0.15)',
        darkBorder: 'rgba(249, 115, 22, 0.35)',
        darkText: '#fb923c',
        hex: '#ffedd5'
    },
    {
        id: 'purple',
        name: 'Morado',
        lightBg: '#f3e8ff',
        lightBorder: '#e9d5ff',
        lightText: '#6b21a8',
        darkBg: 'rgba(168, 85, 247, 0.15)',
        darkBorder: 'rgba(168, 85, 247, 0.35)',
        darkText: '#c084fc',
        hex: '#f3e8ff'
    }
];

export const getContrastTextColor = (hexColor) => {
    if (!hexColor || !hexColor.startsWith('#') || hexColor.length < 7) {
        return '#1e293b';
    }
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? '#1e293b' : '#ffffff';
};

export const getNoteColorStyle = (colorValue, isDark = false) => {
    const preset = NOTE_COLOR_PRESETS.find(
        c => (c.hex || '').toLowerCase() === (colorValue || '').toLowerCase() || c.id === colorValue
    );

    if (preset) {
        if (isDark) {
            return {
                backgroundColor: preset.darkBg,
                borderColor: preset.darkBorder,
                color: preset.darkText
            };
        }
        return {
            backgroundColor: preset.lightBg,
            borderColor: preset.lightBorder,
            color: preset.lightText
        };
    }

    if (colorValue && colorValue.startsWith('#')) {
        const textColor = getContrastTextColor(colorValue);
        return {
            backgroundColor: colorValue,
            borderColor: 'rgba(0, 0, 0, 0.12)',
            color: textColor
        };
    }

    const defaultPreset = NOTE_COLOR_PRESETS[0];
    return isDark ? {
        backgroundColor: defaultPreset.darkBg,
        borderColor: defaultPreset.darkBorder,
        color: defaultPreset.darkText
    } : {
        backgroundColor: defaultPreset.lightBg,
        borderColor: defaultPreset.lightBorder,
        color: defaultPreset.lightText
    };
};

const TicketNotesModal = ({
    show,
    onClose,
    ticket,
    noteToEdit = null,
    isSavingNote = false,
    createNote,
    updateNote
}) => {
    const { config: { isDark } = {} } = useContext(AppContext);
    const { showSuccessToast, showErrorToast } = useToast();

    const [noteText, setNoteText] = useState('');
    const [selectedColor, setSelectedColor] = useState(NOTE_COLOR_PRESETS[0].hex);

    const isEditing = Boolean(noteToEdit && noteToEdit.idTicketNotas);

    useEffect(() => {
        if (show) {
            if (noteToEdit) {
                setNoteText(noteToEdit.note || noteToEdit.vNotaTicket || '');
                setSelectedColor(noteToEdit.noteColor || noteToEdit.vColorNota || NOTE_COLOR_PRESETS[0].hex);
            } else {
                setNoteText('');
                setSelectedColor(NOTE_COLOR_PRESETS[0].hex);
            }
        }
    }, [show, noteToEdit]);

    const ticketId = ticket?.idTicket || ticket?.id || '';
    const displayName = ticket?.customerName
        || ticket?.vCustomerName
        || (ticket?.phone ? `WhatsApp (${ticket.phone})` : `Ticket #${ticketId}`);

    const isCustomColor = !NOTE_COLOR_PRESETS.some(
        p => p.hex.toLowerCase() === (selectedColor || '').toLowerCase()
    );

    const handleSubmit = async (e) => {
        e?.preventDefault();
        const text = noteText.trim();
        if (!text) {
            showErrorToast('Por favor escribe el contenido de la nota.');
            return;
        }

        try {
            if (isEditing) {
                await updateNote({
                    idTicketNotas: noteToEdit.idTicketNotas,
                    note: text,
                    noteColor: selectedColor
                });
                showSuccessToast('Nota actualizada correctamente');
            } else {
                await createNote({
                    note: text,
                    noteColor: selectedColor
                });
                showSuccessToast('Nota agregada correctamente');
            }
            onClose();
        } catch (err) {
            showErrorToast(err.message || (isEditing ? 'Error al actualizar la nota' : 'Error al guardar la nota'));
        }
    };

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            backdrop="static"
            keyboard={!isSavingNote}
        >
            {/* Header */}
            <Modal.Header
                className="px-4 py-3 border-bottom d-flex align-items-center justify-content-between"
                style={{
                    backgroundColor: isDark ? '#121e2d' : '#ffffff',
                    borderColor: isDark ? '#233142' : '#e2e8f0'
                }}
            >
                <div className="d-flex align-items-center gap-2">
                    <div
                        className="rounded-3 p-2 d-flex align-items-center justify-content-center"
                        style={{
                            backgroundColor: isDark ? 'rgba(245, 158, 11, 0.2)' : '#fef3c7',
                            color: isDark ? '#fbbf24' : '#b45309',
                            width: '36px',
                            height: '36px'
                        }}
                    >
                        <FontAwesomeIcon icon={faFileAlt} style={{ fontSize: '1rem' }} />
                    </div>
                    <div>
                        <h5 className="modal-title fw-bold mb-0" style={{ fontSize: '1.02rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                            {isEditing ? 'Editar Nota' : 'Nueva Nota'}
                        </h5>
                        <p className="mb-0 text-muted" style={{ fontSize: '0.78rem' }}>
                            Ticket #{ticketId} &bull; <span className="fw-semibold">{displayName}</span>
                        </p>
                    </div>
                </div>
                <FalconCloseButton onClick={onClose} disabled={isSavingNote} />
            </Modal.Header>

            {/* Body */}
            <Modal.Body
                className="px-4 py-3"
                style={{
                    backgroundColor: isDark ? '#0b141a' : '#f8fafc'
                }}
            >
                <Form onSubmit={handleSubmit}>
                    {/* Selector de color personalizable */}
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fw-semibold text-uppercase" style={{ fontSize: '0.72rem', letterSpacing: '0.5px', color: isDark ? '#94a3b8' : '#64748b' }}>
                            Color de la nota
                        </span>
                        <div className="d-flex align-items-center gap-1.5 flex-wrap">
                            {NOTE_COLOR_PRESETS.map((preset) => (
                                    <button
                                        key={preset.id}
                                        type="button"
                                        onClick={() => setSelectedColor(preset.hex)}
                                        className="rounded-circle border-0 p-0 position-relative d-flex align-items-center justify-content-center"
                                        style={{
                                            width: '22px',
                                            height: '22px',
                                            backgroundColor: preset.hex,
                                        border: selectedColor.toLowerCase() === preset.hex.toLowerCase() ? '2px solid #0284c7' : '1px solid #cbd5e1',
                                        transform: selectedColor.toLowerCase() === preset.hex.toLowerCase() ? 'scale(1.18)' : 'scale(1)',
                                            transition: 'all 0.15s ease',
                                            cursor: 'pointer'
                                        }}
                                        title={preset.name}
                                    >
                                    {selectedColor.toLowerCase() === preset.hex.toLowerCase() && (
                                            <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.6rem', color: preset.lightText }} />
                                        )}
                                    </button>
                            ))}

                            {/* Selector de color personalizado con paleta */}
                            <label
                                className="rounded-circle position-relative d-flex align-items-center justify-content-center m-0"
                                style={{
                                    width: '22px',
                                    height: '22px',
                                    background: 'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
                                    border: isCustomColor ? '2px solid #0284c7' : '1px solid #cbd5e1',
                                    transform: isCustomColor ? 'scale(1.18)' : 'scale(1)',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease'
                                }}
                                title="Elegir color personalizado"
                            >
                                <input
                                    type="color"
                                    value={selectedColor.startsWith('#') && selectedColor.length === 7 ? selectedColor : '#fef3c7'}
                                    onChange={(e) => setSelectedColor(e.target.value)}
                                    style={{
                                        position: 'absolute',
                                        opacity: 0,
                                        width: '100%',
                                        height: '100%',
                                        cursor: 'pointer'
                                    }}
                                />
                                {isCustomColor && (
                                    <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.6rem', color: '#ffffff', textShadow: '0 0 2px rgba(0,0,0,0.8)' }} />
                                )}
                            </label>
                        </div>
                    </div>

                    {/* Textarea para la nota */}
                    <Form.Control
                        as="textarea"
                        rows={4}
                        placeholder="Escribe una nota interna para este ticket o cliente (visitas, acuerdos, detalles técnicos, recordatorios, etc.)..."
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        disabled={isSavingNote}
                        autoFocus
                        style={{
                            backgroundColor: isDark ? '#1e293b' : '#ffffff',
                            color: isDark ? '#f9fafd' : '#0f172a',
                            borderColor: isDark ? '#334155' : '#cbd5e1',
                            fontSize: '0.88rem',
                            resize: 'none'
                        }}
                    />
                </Form>
            </Modal.Body>

            {/* Footer */}
            <Modal.Footer
                className="px-4 py-2.5 border-top d-flex justify-content-end gap-2"
                style={{
                    backgroundColor: isDark ? '#121e2d' : '#ffffff',
                    borderColor: isDark ? '#233142' : '#e2e8f0'
                }}
            >
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={onClose}
                    disabled={isSavingNote}
                    className="px-3"
                    style={{ fontSize: '0.82rem' }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSubmit}
                    disabled={isSavingNote || !noteText.trim()}
                    className="d-flex align-items-center gap-1.5 px-3 py-1 fw-semibold"
                    style={{ fontSize: '0.82rem' }}
                >
                    {isSavingNote ? (
                        <>
                            <Spinner size="sm" animation="border" />
                            <span>Guardando...</span>
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon={isEditing ? faSave : faPlus} />
                            <span>{isEditing ? 'Guardar Cambios' : 'Agregar Nota'}</span>
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

TicketNotesModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    ticket: PropTypes.object.isRequired,
    noteToEdit: PropTypes.object,
    isSavingNote: PropTypes.bool,
    createNote: PropTypes.func.isRequired,
    updateNote: PropTypes.func.isRequired
};

export default TicketNotesModal;
